import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/use-toast'
import { PlusIcon, ResetIcon, UpdateIcon } from '@radix-ui/react-icons'
import { postFetcher } from 'api/swr'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { parseLinearGradient } from 'CanvasCore/utils'
import Title from 'components/ui/Title'
import { useUI } from 'Editor'
import {
  useCallback,
  useEffect,
  useState,
  type ComponentType,
  type FC,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { AdjustColor } from './AdjustColor'
import type { ColorEditorMode } from './types'

const TypedTitle = Title as ComponentType<{
  className?: string
  children?: ReactNode
}>

interface ColorEditorPanelProps {
  mode: ColorEditorMode
  colorId: string
  initialGradient: string
  onClose: () => void
}

const titles: Record<ColorEditorMode, string> = {
  create: 'Create new color',
  edit: 'Update color',
  duplicate: 'Duplicate color',
}

const saveLabels: Record<ColorEditorMode, string> = {
  create: 'Create',
  edit: 'Update',
  duplicate: 'Duplicate',
}

export const ColorEditorPanel: FC<ColorEditorPanelProps> = ({
  mode,
  colorId,
  initialGradient,
  onClose,
}) => {
  const { tid } = useParams<{ tid: string }>()
  const toningId = useUI((state) => state.toningId)
  const selectColor = useEditorStore((state) => state.selectColor)
  const [gradient, setGradient] = useState(initialGradient)

  useEffect(() => {
    setGradient(initialGradient)
  }, [initialGradient, colorId, mode])

  const { trigger, isMutating } = useSWRMutation(
    `/tonings/${toningId}/colors`,
    postFetcher,
    {
      onSuccess: () => {
        const messages: Record<ColorEditorMode, string> = {
          create: 'Color created',
          edit: 'Color updated',
          duplicate: 'Color created',
        }
        toast({
          title: messages[mode],
          className: 'bg-green-500 text-white',
        })
        mutate(['/tonings', { tid }])
        onClose()
      },
    },
  )

  const handleChange = useCallback(
    (value: string) => {
      selectColor(tid!, toningId!, 'default', { id: colorId, color: value })
      setGradient(value)
    },
    [selectColor, tid, toningId, colorId],
  )

  const handleSave = () => {
    const colors = parseLinearGradient(gradient)
    trigger({ tid, colorId, colors })
  }

  return (
    <div className="w-full h-full flex flex-col gap-2 overflow-y-auto">
      <div className="flex items-center shrink-0">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ResetIcon className="w-4 h-4" />
        </Button>
        <TypedTitle className="ml-1">{titles[mode]}</TypedTitle>
        <div className="flex items-center ml-auto gap-2">
          {isMutating ? (
            <>
              <UpdateIcon className="w-4 h-4 mr-2 animate-spin" />
              <span className="text-sm">Saving...</span>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={handleSave}>
              {mode === 'create' ? (
                <PlusIcon className="mr-2 w-4 h-4" />
              ) : (
                <UpdateIcon className="mr-2 w-4 h-4" />
              )}
              <span>{saveLabels[mode]}</span>
            </Button>
          )}
        </div>
      </div>
      <AdjustColor value={gradient} onChange={handleChange} />
    </div>
  )
}
